import React from "react";
import BusinessIcon from "@mui/icons-material/Business";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DevicesIcon from "@mui/icons-material/Devices";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import ForkliftIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";

// Nav config for the LiftConnect Pro sidebar (LcpPortalSidebar). Structurally
// mirrors src/afms/components/EnvironmentalPortal/environmentalNavigation.js — same
// noCollapse + collapse pattern, same gate-utility signatures — so the same
// LcpPortalSidebar chrome can render either tree. Intentionally NO Legacy
// section (per product direction: LCP is a fresh surface).
//
// Layout:
//   - Lift Control Center (clickable header → /lcp/lift-control-center,
//     org-hierarchy + per-lift cards filtered to platform-System=LCP;
//     only for orgs that own LCP lifts)
//       Tickets, Payables
//   - Inspection Command Center (clickable header → /lcp/inspection-command-center,
//     blank placeholder for now)
//       Client Assets, Receivables
//   - Settings
//       Organization, Users, Assets, Devices, Tickets
const primaryRoutes = [
  {
    type: "collapse",
    name: "Lift Control Center",
    key: "lcp-nav-lift-control-center",
    // Per-org backoffice toggle (Edit Organization → LiftConnect Pro →
    // Sidebar Visibility). The whole group and every child carry it.
    requiresLccVisible: true,
    route: "/lcp/lift-control-center",
    noCollapse: true,
    // The customer side: the org's OWN lifts. An inspection company that
    // owns no lifts of its own works entirely out of the Inspection Command
    // Center, so the whole group (and every child) hides until it adds one.
    requiresOwnLifts: true,
    icon: <CarRepairIcon fontSize="small" />,
    collapse: [
      {
        // Lift View — drill to a single lift: its data, inspection history
        // and corrective actions.
        name: "Lift View",
        key: "lcp-nav-lcc-lift-view",
        requiresLccVisible: true,
        route: "/lcp/lift-view",
        icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
      },
      {
        name: "Tickets",
        key: "lcp-nav-lcc-tickets",
        requiresLccVisible: true,
        route: "/lcp/tickets",
        icon: <ConfirmationNumberIcon fontSize="small" />,
      },
      {
        // Payables — invoices sent TO this org by various vendors.
        // Mirrors the AFMS EEC Payables placement: that's where
        // operators triage what they owe.
        name: "Payables",
        key: "lcp-nav-lcc-payables",
        requiresLccVisible: true,
        route: "/lcp/payables",
        icon: <RequestQuoteOutlinedIcon fontSize="small" />,
      },
      {
        // Approval Inbox — engine-driven approver queue (yearly
        // inspections submitted to me).
        name: "Approval Inbox",
        key: "lcp-nav-lcc-approvals-inbox",
        requiresLccVisible: true,
        route: "/lcp/approvals/inbox",
        icon: <InboxOutlinedIcon fontSize="small" />,
      },
      {
        // Sign-off Dashboard — what's signed and what isn't per dealership,
        // how long each report has waited, nudge and print (Jam 8e1c8840).
        name: "Sign-off Dashboard",
        key: "lcp-nav-lcc-signoff-dashboard",
        requiresLccVisible: true,
        route: "/lcp/approvals/signoff-dashboard",
        icon: <PendingActionsOutlinedIcon fontSize="small" />,
      },
      {
        // My Lift Inspections — browse + download surface covering
        // every inspection at a picked location.
        name: "My Lift Inspections",
        key: "lcp-nav-lcc-approvals-my-inspections",
        requiresLccVisible: true,
        route: "/lcp/approvals/my-lift-inspections",
        icon: <FactCheckOutlinedIcon fontSize="small" />,
      },
      {
        // My Lifts — the lift OPERATOR's view: the lifts on their training
        // record and what any tag on them means. Lives on the customer side,
        // not the Inspection Command Center, because the reader is a
        // dealership employee rather than an inspector. Also the QR target on
        // the machine itself, so it carries no requiresNotManaged gate — a
        // managed dealership's operators need it most.
        name: "My Lifts",
        key: "lcp-nav-lcc-my-lifts",
        requiresLccVisible: true,
        route: "/lcp/my-lifts",
        icon: <ForkliftIcon fontSize="small" />,
      },
    ],
  },
  {
    // Inspection Command Center: clickable header navigates to a blank
    // placeholder page for now. Children mirror the AFMS Vendor Command
    // Center pattern (Client Assets + Receivables).
    type: "collapse",
    name: "Inspection Command Center",
    key: "lcp-nav-inspection-command-center",
    // Per-org backoffice toggle, like requiresLccVisible above.
    requiresIccVisible: true,
    route: "/lcp/inspection-command-center",
    noCollapse: true,
    // Enterprise-side surface: hidden for orgs that are themselves managed
    // by an enterprise (a managed dealership just runs its Lift Control
    // Center). The group gate cascades to hide all children too; they
    // carry the same flag for defensiveness.
    requiresNotManaged: true,
    // The inspecting side's workspace — also hidden from a plain User who
    // holds no inspector role (a client's Service Manager, Jam 6db3d703).
    requiresInspectorRole: true,
    icon: <HomeOutlinedIcon fontSize="small" />,
    collapse: [
      {
        name: "Client Assets",
        key: "lcp-nav-icmd-client-assets",
        requiresIccVisible: true,
        route: "/lcp/client-assets",
        icon: <DevicesIcon fontSize="small" />,
        requiresNotManaged: true,
        requiresInspectorRole: true,
      },
      {
        // Receivables — invoices this org has issued to client orgs.
        name: "Receivables",
        key: "lcp-nav-icmd-receivables",
        requiresIccVisible: true,
        route: "/lcp/receivables",
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
        requiresNotManaged: true,
        requiresInspectorRole: true,
      },
      {
        // Schedule — calendar of past inspection work per inspector.
        name: "Schedule",
        key: "lcp-nav-icmd-schedule",
        requiresIccVisible: true,
        route: "/lcp/schedule",
        icon: <CalendarMonthOutlinedIcon fontSize="small" />,
        requiresNotManaged: true,
        requiresInspectorRole: true,
      },
      {
        // Inspection Results Report — the client-facing document for a scope
        // of lifts: findings, open corrective actions and where each report
        // sits in the approval route. Schedule answers "what did we do";
        // this answers "what did we find".
        name: "Inspection Results",
        key: "lcp-nav-icmd-inspection-results",
        requiresIccVisible: true,
        route: "/lcp/inspection-results",
        icon: <SummarizeOutlinedIcon fontSize="small" />,
        requiresNotManaged: true,
        requiresInspectorRole: true,
      },
      {
        // Backoffice page mounted here so the user can evaluate it
        // inside the LCP shell. Same component as /inspections in the
        // legacy nav.
        name: "Inspections",
        key: "lcp-nav-icmd-inspections",
        requiresIccVisible: true,
        route: "/lcp/inspections",
        icon: <FactCheckOutlinedIcon fontSize="small" />,
        requiresNotManaged: true,
        requiresInspectorRole: true,
      },
    ],
  },
  {
    // Personal cross-system inbox — the same page and the same
    // notifications-api feed the environmental portal mounts at
    // /afms/inbox, so messages produced by ANY system land here too.
    // Top-level and ungated, like its AFMS counterpart: the recipient is
    // the caller, resolved server-side from the bearer token.
    type: "collapse",
    name: "My Inbox",
    key: "lcp-nav-inbox",
    route: "/lcp/inbox",
    noCollapse: true,
    // Jam 7f5d0022: the unread count showed beside Environmental's "My Inbox" but not here.
    badge: "inbox",
    icon: <MailOutlineIcon fontSize="small" />,
  },
];

// Approvals subgroup — nested under Settings (Settings → Approvals →
// Rules/Gates/Notifications). Same three children as
// environmentalNavigation's Approvals collapse; backed by lcp-api
// approval-* endpoints (port-in-progress from afms-api).
const approvalsSubgroup = {
  type: "collapse",
  name: "Approvals",
  key: "lcp-nav-settings-approvals",
  icon: <RuleFolderOutlinedIcon fontSize="small" />,
  collapse: [
    {
      name: "Approval Rules",
      key: "lcp-nav-approvals-rules",
      route: "/lcp/settings/approval-rules",
      icon: <GavelOutlinedIcon fontSize="small" />,
    },
    {
      name: "Approval Gates",
      key: "lcp-nav-approvals-gates",
      route: "/lcp/settings/approval-gates",
      icon: <AccountTreeOutlinedIcon fontSize="small" />,
    },
    {
      name: "Approval Notifications",
      key: "lcp-nav-approvals-notifications",
      route: "/lcp/settings/approval-notifications",
      icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
    },
  ],
};

const settingsGroup = {
  // LCP-rebranded Settings group. Sub-items lift their data from the existing
  // flex2 Org Admin / Assets / Devices pages (same as AFMS Settings does) —
  // they auto-scope to the caller's own org via JWT + [AllowOwnOrgAccess].
  type: "collapse",
  name: "Settings",
  key: "lcp-nav-settings",
  // Org set-up belongs to whoever runs the org. A plain User — a client's
  // Service Manager, say — has nothing to configure here (Jam 713b3c51).
  requiresAdminRole: true,
  icon: <SettingsOutlinedIcon fontSize="small" />,
  collapse: [
    {
      name: "Organization",
      key: "lcp-nav-settings-organization",
      route: "/lcp/settings/organization",
      icon: <BusinessIcon fontSize="small" />,
    },
    {
      name: "Users",
      key: "lcp-nav-settings-users",
      route: "/lcp/settings/users",
      icon: <PeopleAltIcon fontSize="small" />,
    },
    {
      name: "Assets",
      key: "lcp-nav-settings-assets",
      route: "/lcp/settings/assets",
      icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
    },
    {
      // Lift Onboarding Wizard — walk a rooftop bay by bay and add its lifts.
      // Inherits the group's requiresAdminRole: it creates locations and
      // assets, which only owners/admins may do.
      name: "Lift Onboarding",
      key: "lcp-nav-settings-lift-onboarding",
      route: "/lcp/settings/lift-onboarding",
      icon: <AddLocationAltOutlinedIcon fontSize="small" />,
    },
    {
      name: "Devices",
      key: "lcp-nav-settings-devices",
      route: "/lcp/settings/devices",
      icon: <DevicesIcon fontSize="small" />,
    },
    {
      name: "Tickets",
      key: "lcp-nav-settings-tickets",
      route: "/lcp/settings/tickets",
      icon: <ConfirmationNumberIcon fontSize="small" />,
    },
    approvalsSubgroup,
    {
      // Organization-wide LCP settings — grouped as a nested dropdown so
      // future LCP-scoped preferences (certificate branding, sign-off
      // defaults, etc.) can nest into their own sub-menus without
      // stuffing the top-level Settings list. First tenant: Certificate
      // Setup → Upload Logo (inspection-form logo, stored in S3 via
      // OrganizationBrandController).
      type: "collapse",
      name: "LCP Settings",
      key: "lcp-nav-settings-lcp-settings",
      icon: <SettingsOutlinedIcon fontSize="small" />,
      collapse: [
        {
          name: "General Settings",
          key: "lcp-nav-settings-lcp-settings-general",
          route: "/lcp/settings/general",
          icon: <TuneOutlinedIcon fontSize="small" />,
        },
        {
          type: "collapse",
          name: "Certificate Setup",
          key: "lcp-nav-settings-lcp-settings-certificate-setup",
          icon: <WorkspacePremiumOutlinedIcon fontSize="small" />,
          collapse: [
            {
              name: "Upload Logo",
              key: "lcp-nav-settings-lcp-settings-certificate-setup-upload-logo",
              route: "/lcp/settings/certificate-setup/upload-logo",
              icon: <UploadFileOutlinedIcon fontSize="small" />,
            },
            {
              name: "Verbage",
              key: "lcp-nav-settings-lcp-settings-certificate-setup-verbage",
              route: "/lcp/settings/certificate-setup/verbage",
              icon: <ArticleOutlinedIcon fontSize="small" />,
            },
            {
              name: "Results / Actions",
              key: "lcp-nav-settings-lcp-settings-certificate-setup-results-actions",
              route: "/lcp/settings/certificate-setup/results-actions",
              icon: <RuleOutlinedIcon fontSize="small" />,
            },
          ],
        },
        {
          // Org-owned inspection reference catalogs. Each org maintains its
          // own make/models + task / comment / deferral templates; the
          // inspection form reads the acting org's rows.
          type: "collapse",
          name: "Resources Setup",
          key: "lcp-nav-settings-lcp-settings-resources",
          icon: <InboxOutlinedIcon fontSize="small" />,
          collapse: [
            {
              name: "Lift Models",
              key: "lcp-nav-settings-lcp-settings-resources-lift-models",
              route: "/lcp/resources/lift-models",
              icon: <CarRepairIcon fontSize="small" />,
            },
            {
              name: "Tasks",
              key: "lcp-nav-settings-lcp-settings-resources-tasks",
              route: "/lcp/resources/tasks",
              icon: <AssignmentTurnedInOutlinedIcon fontSize="small" />,
            },
            {
              name: "Comments",
              key: "lcp-nav-settings-lcp-settings-resources-comments",
              route: "/lcp/resources/comments",
              icon: <ArticleOutlinedIcon fontSize="small" />,
            },
            {
              name: "Deferrals",
              key: "lcp-nav-settings-lcp-settings-resources-deferrals",
              route: "/lcp/resources/deferrals",
              icon: <RuleOutlinedIcon fontSize="small" />,
            },
          ],
        },
      ],
    },
  ],
};

// Management Core group — same shape used in the AFMS + Energy portals.
// Shown to any org that isn't itself managed by an enterprise (see
// itemPassesGate below). Enterprise-OWNER enforcement now lives at the
// API layer only, mirroring the AFMS "Management Core" group.
const enterpriseGroup = {
  type: "collapse",
  name: "Management Core",
  key: "lcp-nav-enterprise",
  icon: <ApartmentOutlinedIcon fontSize="small" />,
  requiresNotManaged: true,
  collapse: [
    {
      name: "Manage my Clients",
      key: "lcp-nav-enterprise-clients",
      route: "/lcp/enterprise/manage-clients",
      icon: <HandshakeOutlinedIcon fontSize="small" />,
      requiresNotManaged: true,
    },
    {
      name: "Manage my Vendors",
      key: "lcp-nav-enterprise-vendors",
      route: "/lcp/enterprise/manage-vendors",
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
      requiresNotManaged: true,
    },
  ],
};

// No Legacy section — LCP is a fresh surface and explicitly omits the
// compatibility cruft that lives under AFMS Legacy.
const lcpPortalRoutes = [...primaryRoutes, settingsGroup, enterpriseGroup];

// Primary roles that run the org, and the secondary roles that inspect —
// either one opens the Inspection Command Center.
const COMMAND_CENTER_PRIMARY_ROLES = new Set(["OWNER", "ADMIN", "SUPER_ADMIN"]);
const INSPECTOR_SECONDARY_ROLES = new Set([
  "inspector",
  "lift_inspector_l1",
  "lift_inspector_l2",
  "lift_inspector_l3",
]);

// Per-item gate. LCP has no per-action capability matrix yet so most
// items pass, but the Enterprise menu does need to gate on org-level
// flags so a non-enterprise org or non-OWNER user doesn't see it.
export const itemPassesGate = (route, _allowedActions, _isPlatformAdmin, orgContext = {}) => {
  if (!route) return false;
  // Per-org sidebar toggles from Organization.showLiftControlCenter and
  // Organization.showInspectionCommandCenter (backoffice → Edit
  // Organization → LiftConnect Pro). Explicit `false` hides; anything else
  // (true / undefined / stale session) keeps the group, like the AFMS ones.
  if (route.requiresLccVisible && orgContext.showLiftControlCenter === false) return false;
  if (route.requiresIccVisible && orgContext.showInspectionCommandCenter === false) {
    return false;
  }
  const isAdmin = COMMAND_CENTER_PRIMARY_ROLES.has((orgContext.role || "").toUpperCase());
  if (route.requiresAdminRole && !isAdmin) return false;
  if (route.requiresInspectorRole) {
    const isInspector = (orgContext.secondaryRoles || []).some((kind) =>
      INSPECTOR_SECONDARY_ROLES.has(String(kind).toLowerCase())
    );
    if (!isAdmin && !isInspector) return false;
  }
  // Hide from orgs that are themselves managed by an enterprise (they
  // don't manage sub-orgs of their own). Fail-open: only an explicit
  // isManaged===true hides. Mirrors the AFMS "Management Core" gate.
  if (route.requiresNotManaged && orgContext.isManaged === true) return false;
  // Hide from an org that owns no LCP lifts. Fail-open: only an explicit
  // hasOwnLifts===false hides, so the group stays put while the count loads
  // or if it fails. A managed dealership always keeps it — the Lift Control
  // Center is its whole surface, even before its first lift is added.
  if (
    route.requiresOwnLifts &&
    orgContext.hasOwnLifts === false &&
    orgContext.isManaged !== true
  ) {
    return false;
  }
  // Legacy gate kept for any route still using it: requires the org-level
  // isEnterprise flag AND the caller be the org's primary OWNER.
  if (route.requiresEnterpriseOwner) {
    const isOwner = (orgContext.role || "").toUpperCase() === "OWNER";
    const isEnterprise = orgContext.isEnterpriseOrganization === true;
    if (!isOwner || !isEnterprise) return false;
  }
  return true;
};

const filterRoutesByCapabilities = (routes, allowedActions, isPlatformAdmin, orgContext) =>
  routes
    .map((route) => {
      if (route.type === "title" || route.type === "divider") return route;
      if (!itemPassesGate(route, allowedActions, isPlatformAdmin, orgContext)) return null;
      if (!route.collapse) return route;
      const collapse = filterRoutesByCapabilities(
        route.collapse,
        allowedActions,
        isPlatformAdmin,
        orgContext
      );
      if (route.type === "collapse" && collapse.length === 0 && !route.route) return null;
      return { ...route, collapse };
    })
    .filter(Boolean);

const stripOrphanTitles = (routes) =>
  routes.filter((route, index) => {
    if (route.type !== "title") return true;
    for (let i = index + 1; i < routes.length; i += 1) {
      if (routes[i].type === "title") return false;
      if (routes[i].type !== "divider") return true;
    }
    return false;
  });

/**
 * Builds the LiftConnect Pro sidebar. Capabilities + orgContext are
 * accepted for signature parity with getEnvironmentalPortalRoutes — they're
 * ignored for now since LCP doesn't have per-action gates yet.
 */
export const getLcpPortalRoutes = (capabilities, orgContext = {}) =>
  stripOrphanTitles(
    filterRoutesByCapabilities(lcpPortalRoutes, null, false, orgContext)
  );

export default lcpPortalRoutes;
